import { groupBy } from 'lodash';
import { Fragment } from 'react';
import { parseJson } from '~/helpers';
import { formatDate } from '~/helpers/formatDate';
import { useContacts } from '~/routes/resources/contacts';
import {
  applyAttributeCategoryFromName,
  isDisplayableAttribute,
} from './attributeDisplay';
import { Files } from './FileLink';
import { Gallery } from './job/JobView/Feed/Gallery';
import { Linkify } from './job/JobView/Feed/Linkify';
import { Contact } from './job/JobView/JobReadView/Bubbles';

export { hasDisplayableAttributes } from './attributeDisplay';

type Props = {
  attributes: {
    type?: string;
    category?: string | null;
    name: string;
    value: string[];
    attachments?:
      | {
          id: string;
          kind: string;
          ext: string;
          thumb: string;
          url: string;
          originalFilename: string;
        }[]
      | null;
  }[];
  contacts?: Contact[];
};

const formatDateSafe = (date: string, pattern: string) => {
  try {
    return formatDate(date, 'PP');
  } catch (err) {
    return null;
  }
};

export function AttributeView({ attributes, contacts: contactsProp }: Props) {
  const contacts = contactsProp ?? useContacts();

  const nonEmptyAttributes = attributes.filter(isDisplayableAttribute);

  const processedAttributes = nonEmptyAttributes.map(
    applyAttributeCategoryFromName
  );

  const grouped = groupBy(processedAttributes, (item) => {
    return item.category ?? 'General';
  });

  return (
    <>
      {Object.keys(grouped).map((key) => (
        <Fragment key={key}>
          <div className='pt-3 text-base font-semibold text-primary'>{key}</div>

          {grouped[key].map((attribute) => {
            const oldValue = parseJson(attribute.value[0]);
            const displayValue =
              attribute.type === 'contact' && Array.isArray(oldValue) ? (
                contacts
                  .filter(({ id }) => oldValue.includes(id))
                  .map(({ name }) => name)
                  .join('; ')
              ) : attribute.type === 'date' ? (
                formatDateSafe(attribute.value[0], 'PP')
              ) : (
                <Linkify>{attribute.value.join('; ')}</Linkify>
              );

            return (
              <Fragment key={attribute.name}>
                {attribute.type === 'attachment' && attribute.attachments ? (
                  <div className='mb-3 indent-2 leading-5'>
                    <div className='font-[500]'>{attribute.name}:</div>
                    <Files attachments={attribute.attachments} />
                    <Gallery attachments={attribute.attachments} />
                  </div>
                ) : grouped[key].length === 1 &&
                  key === grouped[key][0].name ? (
                  <p className='break-words leading-5'>{displayValue}</p>
                ) : (
                  <div className='flex items-start gap-2 py-1 leading-5'>
                    <div className="shrink-0 indent-2 font-[500] after:content-[':']">
                      {attribute.name}
                    </div>
                    <p className='min-w-0 flex-1 whitespace-pre-line break-words text-secondary'>
                      {displayValue}
                    </p>
                  </div>
                )}
              </Fragment>
            );
          })}
        </Fragment>
      ))}
    </>
  );
}
